import api from "../../services/api/axios";

const toProperty = (record) => {
  const imageUrls = Array.isArray(record.image_urls) ? record.image_urls.filter(Boolean) : [];
  const images = record.image_url
    ? [record.image_url, ...imageUrls.filter((image) => image !== record.image_url)]
    : imageUrls;
  return {
  id: record.id,
  name: record.name || "",
  propertyType: record.property_type || "",
  status: record.status === "Active" ? "Occupied" : record.status || "Occupied",
  yearBuilt: record.year_built ?? "",
  numberOfFloors: record.number_of_floors ?? "",
  lotSizeAcres: record.lot_size_acres ?? "",
  buildingDescription: record.building_description || "",
  utilitiesAvailable: record.utilities_available || "",
  image: images[0] || "",
  images,
  streetAddress: record.street_address || "",
  city: record.city || "",
  state: record.state_province || "",
  zipCode: record.postal_code || "",
  country: record.country || "",
  squareFeet: record.square_footage ?? "",
  latitude: record.latitude ?? "",
  longitude: record.longitude ?? "",
  contacts: record.contacts || [],
  documents: record.documents || [],
  notes: record.notes || [],
  occupancyRecords: record.occupancy_records || [],
  leases: record.leases || [],
  financialInformation: record.financial_information || {},
  createdAt: record.created_at,
  updatedAt: record.updated_at,
  };
};

const toPayload = (property) => {
  const images = Array.isArray(property.images)
    ? property.images.filter(Boolean)
    : property.image
      ? [property.image]
      : [];
  return {
  name: property.name.trim(),
  property_type: property.propertyType?.trim() || null,
  status: property.status?.trim() || "Occupied",
  year_built: property.yearBuilt === "" || property.yearBuilt == null ? null : Number(property.yearBuilt),
  number_of_floors: property.numberOfFloors === "" || property.numberOfFloors == null ? null : Number(property.numberOfFloors),
  lot_size_acres: property.lotSizeAcres === "" || property.lotSizeAcres == null ? null : Number(property.lotSizeAcres),
  building_description: property.buildingDescription?.trim() || null,
  utilities_available: property.utilitiesAvailable?.trim() || null,
  image_url: images[0] || null,
  image_urls: images,
  street_address: property.streetAddress.trim() || null,
  city: property.city.trim() || null,
  state_province: property.state.trim() || null,
  postal_code: property.zipCode.trim() || null,
  country: property.country?.trim() || null,
  square_footage:
    property.squareFeet === "" || property.squareFeet == null
      ? null
      : Number(property.squareFeet),
  latitude: property.latitude === "" || property.latitude == null ? null : Number(property.latitude),
  longitude: property.longitude === "" || property.longitude == null ? null : Number(property.longitude),
  };
};

const workspacePayload = (property) => ({
  contacts: property.contacts || [],
  documents: property.documents || [],
  notes: property.notes || [],
  occupancy_records: property.occupancyRecords || [],
  leases: property.leases || [],
  financial_information: property.financialInformation || {},
  latitude: property.latitude === "" || property.latitude == null ? null : Number(property.latitude),
  longitude: property.longitude === "" || property.longitude == null ? null : Number(property.longitude),
});

export async function listProperties() {
  const { data } = await api.get("/properties");
  return data.map(toProperty);
}

export async function getProperty(propertyId) {
  const { data } = await api.get(`/properties/${propertyId}`);
  return toProperty(data);
}

export async function createProperty(property) {
  const { data } = await api.post("/properties", toPayload(property));
  return toProperty(data);
}

export async function updateProperty(propertyId, property) {
  const { data } = await api.patch(`/properties/${propertyId}`, toPayload(property));
  return toProperty(data);
}

export async function updatePropertyWorkspace(propertyId, property) {
  const { data } = await api.patch(`/properties/${propertyId}`, workspacePayload(property));
  return toProperty(data);
}

const phase3Path = (propertyId, resource, recordId) =>
  `/properties/${propertyId}/${resource}${recordId == null ? "" : `/${recordId}`}`;

const toPhase3Record = (record) => ({
  ...record,
  propertyId: record.property_id,
  organizationId: record.organization_id,
  userId: record.user_id,
  entityType: record.entity_type,
  entityId: record.entity_id,
  occurredAt: record.occurred_at,
  createdAt: record.created_at,
  updatedAt: record.updated_at,
  storageKey: record.storage_key,
  contentType: record.content_type,
  fileSize: record.file_size,
  uploadedByUserId: record.uploaded_by_user_id,
  uploadedByName: record.uploaded_by_name,
  contactType: record.contact_type,
  authorUserId: record.author_user_id,
  authorName: record.author_name,
  activityUserName: record.user_name,
});

const recordPayload = (resource, record) => {
  const fields = {
    documents: ["name", "category", "url", "description"],
    contacts: ["name", "company", "role", "email", "phone", "contact_type"],
    notes: ["title", "body"],
  }[resource];
  return Object.fromEntries(fields
    .filter((field) => !(resource === "documents" && field === "url" && !record[field]))
    .map((field) => [field, (field === "contact_type" ? record.contactType : record[field])?.trim() || null]));
};

export async function listPropertyRecords(propertyId, resource) {
  const { data } = await api.get(phase3Path(propertyId, resource));
  return data.map(toPhase3Record);
}

export async function createPropertyRecord(propertyId, resource, record) {
  const { data } = await api.post(phase3Path(propertyId, resource), recordPayload(resource, record));
  return toPhase3Record(data);
}

export async function updatePropertyRecord(propertyId, resource, recordId, record) {
  const { data } = await api.patch(phase3Path(propertyId, resource, recordId), recordPayload(resource, record));
  return toPhase3Record(data);
}

export async function deletePropertyRecord(propertyId, resource, recordId) {
  await api.delete(phase3Path(propertyId, resource, recordId));
}

export async function listPropertyActivity(propertyId) {
  return listPropertyRecords(propertyId, "activity");
}

export async function uploadPropertyDocument(propertyId, file, category) {
  const form = new FormData();
  form.append("file", file);
  form.append("category", category);
  const { data } = await api.post(`/properties/${propertyId}/documents/upload`, form);
  return toPhase3Record(data);
}

async function propertyDocumentBlob(propertyId, documentId, action) {
  const { data } = await api.get(`/properties/${propertyId}/documents/${documentId}/${action}`, { responseType: "blob" });
  return data;
}

export async function downloadPropertyDocument(propertyId, document) {
  const blob = await propertyDocumentBlob(propertyId, document.id, "download");
  const url = URL.createObjectURL(blob);
  const anchor = window.document.createElement("a");
  anchor.href = url;
  anchor.download = document.name;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function previewPropertyDocument(propertyId, documentId) {
  const blob = await propertyDocumentBlob(propertyId, documentId, "preview");
  return URL.createObjectURL(blob);
}

const phase4Path = (propertyId, resource, recordId) =>
  `/properties/${propertyId}/${resource}${recordId == null ? "" : `/${recordId}`}`;

const toPhase4Record = (record) => ({
  ...record,
  propertyId: record.property_id,
  organizationId: record.organization_id,
  tenantName: record.tenant_name,
  contactName: record.contact_name,
  startDate: record.start_date,
  endDate: record.end_date,
  monthlyRent: record.monthly_rent,
  createdAt: record.created_at,
  updatedAt: record.updated_at,
});

const phase4Payload = (resource, record) => resource === "tenants"
  ? {
      tenant_name: record.tenantName.trim(),
      contact_name: record.contactName?.trim() || null,
      email: record.email?.trim() || null,
      phone: record.phone?.trim() || null,
    }
  : {
      tenant: record.tenant.trim(),
      start_date: record.startDate,
      end_date: record.endDate,
      status: record.status,
    };

export async function listPropertyPhase4Records(propertyId, resource) {
  const { data } = await api.get(phase4Path(propertyId, resource));
  return data.map(toPhase4Record);
}

export async function createPropertyPhase4Record(propertyId, resource, record) {
  const { data } = await api.post(phase4Path(propertyId, resource), phase4Payload(resource, record));
  return toPhase4Record(data);
}

export async function updatePropertyPhase4Record(propertyId, resource, recordId, record) {
  const { data } = await api.patch(phase4Path(propertyId, resource, recordId), phase4Payload(resource, record));
  return toPhase4Record(data);
}

export async function deletePropertyPhase4Record(propertyId, resource, recordId) {
  await api.delete(phase4Path(propertyId, resource, recordId));
}
